import { Component, computed, effect, input, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, AbstractControl, FormControl, ReactiveFormsModule } from '@angular/forms';
import { FormInput, FormInputType } from '@shared/models/form-input.model';
import { CEP_MASK, CNPJ_MASK, CPF_MASK } from '@shared/constants';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatIcon } from '@angular/material/icon';
import { ZInputComponent } from '../z-input/z-input.component';
import { ZInputPasswordComponent } from '../z-input-password/z-input-password.component';
import { ZSelectComponent } from '../z-select/z-select.component';
import { DatePickerComponent } from '../dates/datepicker/datepicker.component';
import { MonthPickerComponent } from '../dates/monthpicker/monthpicker.component';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { MatFormField, MatLabel, MatInput } from '@angular/material/input';
import { CurrencyMaskModule } from 'ng2-currency-mask';
import { FileUploaderComponent } from '../file-uploader/file-uploader.component';
import { AsyncPipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'z-form-grid',
    templateUrl: './z-form-grid.component.html',
    styleUrls: ['./z-form-grid.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatCheckbox, ReactiveFormsModule, MatIcon, ZInputComponent, ZInputPasswordComponent, ZSelectComponent, DatePickerComponent, MonthPickerComponent, MatRadioGroup, MatRadioButton, MatFormField, MatLabel, MatInput, CurrencyMaskModule, FileUploaderComponent, AsyncPipe, TranslatePipe]
})
export class ZFormGridComponent {
    readonly MASK = { CPF: CPF_MASK, CNPJ: CNPJ_MASK, CEP: CEP_MASK };
    readonly FormInputType = FormInputType;
    readonly CEP_MASK = CEP_MASK; // Expondo para o template conforme o case CEP

    readonly formGroupRef = input.required<FormGroup>();
    readonly inputs = input<FormInput[]>([]);
    readonly gridClass = input<string>('row');
    readonly initialCollapsed = input<boolean>(false);

    private collapsedSet = signal<Set<string>>(new Set());
    private formValueVersion = signal(0);

    constructor() {
        this.setupInitialCollapseEffect();
        this.setupFormValueTracker();
    }

    private setupFormValueTracker() {
        effect((onCleanup) => {
            const sub = this.formGroupRef().valueChanges.subscribe(() => {
                this.formValueVersion.update(v => v + 1);
            });
            onCleanup(() => sub.unsubscribe());
        });
    }

    private setupInitialCollapseEffect() {
        effect(() => {
            const allInputs = this.inputs();
            if (allInputs.length === 0) return;

            const newSet = new Set<string>();
            let sectionsFound = 0;
            const openLimit = this.initialCollapsed() ? 0 : (allInputs[0]?.type === FormInputType.DIVISOR ? 2 : 1);

            allInputs.forEach(input => {
                if (this.isSectionType(input.type)) {
                    sectionsFound++;
                    if (sectionsFound > openLimit) newSet.add(input.name);
                }
            });
            this.collapsedSet.set(newSet);
        }, { allowSignalWrites: true });
    }

    readonly sectionStatus = computed(() => {
        this.formValueVersion();
        const statusMap = new Map<string, { hasRequired: boolean; isComplete: boolean }>();
        let activeDivisor: string | null = null;

        for (const input of this.inputs()) {
            if (input.type === FormInputType.DIVISOR) {
                activeDivisor = input.name;
                statusMap.set(input.name, { hasRequired: false, isComplete: true });
                continue;
            }
            // SUBDIVISOR é só sinalização visual; não agrega status.
            if (input.type === FormInputType.SUBDIVISOR) {
                continue;
            }
            // Uploads (documentos) ficam soltos ao final e não pertencem a nenhuma seção:
            // não devem contribuir para o status de obrigatoriedade do divisor ativo (ex.: PPE).
            if (input.type === FormInputType.FILE) {
                continue;
            }
            if (input.required) {
                const control = input.formControlName ? this.formGroupRef().get(input.formControlName) : null;
                if (this.isFieldActive(input, control)) {
                    const isValid = this.isControlValid(control);
                    if (activeDivisor) this.updateStatusMap(statusMap, activeDivisor, isValid);
                }
            }
        }
        return statusMap;
    });

    readonly fieldVisibility = computed(() => {
        const collapsed = this.collapsedSet();
        let sectionIsCollapsed = false;
        return this.inputs().map(input => {
            if (this.isSectionType(input.type)) {
                sectionIsCollapsed = collapsed.has(input.name);
                return true;
            }
            // Uploads (documentos) ficam sempre visíveis, apartados das seções expansíveis.
            if (input.type === FormInputType.FILE) {
                return true;
            }
            return !sectionIsCollapsed;
        });
    });

    // --- Métodos chamados pelo Template ---

    getControl(name: string): FormControl {
        return (this.formGroupRef().get(name) as FormControl) || new FormControl();
    }

    getInputId(input: FormInput): string {
        return input.id ?? input.formControlName ?? '';
    }

    isSectionCollapsed(name: string): boolean {
        return this.collapsedSet().has(name);
    }

    getSectionStatus(name: string) {
        return this.sectionStatus().get(name) || { hasRequired: false, isComplete: false };
    }

    toggleSection(name: string) {
        this.collapsedSet.update(set => {
            const newSet = new Set(set);
            newSet.has(name) ? newSet.delete(name) : newSet.add(name);
            return newSet;
        });
    }

    // Apenas DIVISOR é seção colapsável. SUBDIVISOR é só uma sinalização visual
    // que segue a visibilidade da seção (DIVISOR) em que está.
    private isSectionType = (type: FormInputType | undefined) =>
        type === FormInputType.DIVISOR;

    private isFieldActive(input: FormInput, control: AbstractControl | null): boolean {
        return !input.class?.includes('d-none') && !control?.disabled;
    }

    private isControlValid(control: AbstractControl | null): boolean {
        if (!control) return false;
        return control.valid && control.value !== null && control.value !== undefined && control.value !== '';
    }

    private updateStatusMap(map: Map<string, any>, name: string, isValid: boolean) {
        const status = map.get(name);
        if (status) {
            status.hasRequired = true;
            if (!isValid) status.isComplete = false;
        }
    }
}